/**
 * Shrink Wrap modifier — dispatcher into the WASM kernel.
 *
 * The geometry kernel (closest-point-on-triangle, grid-accelerated nearest
 * surface, ray–triangle project, area-weighted normal fallback) lives in
 * Rust at `apps/web/src/wasm/src/shrinkwrap.rs`. This file is a thin
 * dispatcher that preserves the modifier-pipeline contract: take input
 * geometry + a shrinkwrap modifier descriptor, hand back displaced positions
 * with stale normals and identity provenance.
 */

import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import { identityProvenance } from "../../modeling/provenance.ts";
import { evaluateShrinkwrap } from "../../wasm/shrinkwrap.ts";
import type {
  ModifierEvaluationInput,
  ModifierGeometry,
} from "./modifierTypes.ts";

export function shrinkwrapCore(
  geo: ModifierGeometry,
  mod: Extract<BlendModifier, { kind: "shrinkwrap" }>,
  input?: ModifierEvaluationInput,
): ModifierGeometry {
  if (!mod.targetObjectName || !input?.sourceGeometries) {
    return geo;
  }
  const targetGeo = input.sourceGeometries[mod.targetObjectName];
  if (
    !targetGeo ||
    targetGeo.positions.length === 0 ||
    targetGeo.indices.length === 0
  ) {
    return geo;
  }

  const newPositions = evaluateShrinkwrap({
    srcPositions: geo.positions,
    srcIndices: geo.indices,
    srcNormals: geo.normals,
    tgtPositions: targetGeo.positions,
    tgtIndices: targetGeo.indices,
    tgtNormals: targetGeo.normals,
    wrapMode: mod.wrapMode,
    projectAxis: mod.projectAxis,
    projectPositive: mod.projectPositive,
    projectNegative: mod.projectNegative,
    offset: mod.offset,
    vertexGroupIndices: mod.vertexGroupIndices ?? null,
  });

  return {
    ...geo,
    positions: newPositions,
    // Normals are stale after displacement — let the renderer recompute them.
    normals: null,
    // Vertex count is unchanged; provenance is identity.
    provenance: identityProvenance(newPositions.length / 3),
  };
}
